import React, { useContext, useEffect, useRef } from "react";
import { WindowLevelTool, Enums as csToolsEnums, annotation as csAnnotation } from "@cornerstonejs/tools";
import { eventTarget } from "@cornerstonejs/core";
import { isMobile } from "react-device-detect";
import { renderViewports } from "../../helpers/CornerstoneViewportService";
import { MOBILE_MEASURE_TOOLS } from "../../../../constants/Constants";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import MobileViewerContext from "../../../../context/studyviewer/MobileViewerContext";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import themeContext from "../../../../context/theme/ThemeContext";
import { useTranslation } from "react-i18next";
import Tool from "../Tools/ToolComponents/Tool";
import MobileMeasureSheet from "./MobileMeasureSheet";
import MobileLayoutMenu from "./MobileLayoutMenu";
import MobileRotateMenu from "./MobileRotateMenu";
import MeasureAngle from "../../../../components/SVGComponents/Tools/MeasureAngle";
import DropDown from "../../../../components/SVGComponents/Tools/DropDown";
import MobileMoreMenu from "./MobileMoreMenu";
import { toolStyle } from "../../styles/styles";
import KeyImages from "../Tools/ToolComponents/KeyImage/KeyImages";

const toolBarFlex = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Window Level stays on the main bar (rendered via the shared <Tool>).
const WINDOW_LEVEL_TOOL = {
  key: WindowLevelTool.toolName,
  NotFor: [],
  name: "viewer.tools.window",
};

const mobileLabelStyle = {
  fontSize: "9px",
  lineHeight: "100%",
  fontWeight: "400",
  color: "var(--mobile-color-txt)",
  display: "block",
  width: "60px",
  textAlign: "center",
  marginTop: "4px",
};

const iconBoxStyle = {
  height: "65%",
  width: "60px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// MedDream-style curated bottom bar: Thumbnails · Window Level · Measure ·
// Rotate · Layout · More. Everything else lives in the "More" grid.
const MobileToolbar = () => {
  const { theme } = useContext(themeContext);
  const { activeModality } = useContext(ThumbnailContext);
  const { viewportLoadingComplete, activeTool } =
    useContext(ViewportContext);
  const { showMeasureSheet, setShowMeasureSheet } =
    useContext(MobileViewerContext);
  const { showKeyImagesAndTextAnnotation } = useContext(studyReportContext);
  const { t } = useTranslation();

  const isMeasureActive =
    showMeasureSheet ||
    MOBILE_MEASURE_TOOLS.some((measureTool) => measureTool.key === activeTool);

  const measureRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (measureRef.current && !measureRef.current.contains(event.target)) {
        setShowMeasureSheet(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [setShowMeasureSheet]);

  //  on touch, selecting an annotation (tap, or finishing a draw) updates
  // Cornerstone's selection but didn't repaint the viewport, so the selected (yellow) vs
  // unselected (green) colors never refreshed on mobile. Force a re-render on selection change so
  // it matches the desktop basic viewer (and the selected annotation is the one "Delete Selected"
  // removes).
  useEffect(() => {
    if (!isMobile) return;
    const handleSelectionChange = () => renderViewports();
    const handleCompleted = (evt) => {
      const annotation = evt?.detail?.annotation;
      const uid = annotation?.annotationUID;
      if (!uid) return;
      try {
        if (annotation) annotation.highlighted = false;
        csAnnotation.selection.setAnnotationSelected(uid, true, false);
      } catch (e) {
        console.warn("[mobile-annotation-select] setAnnotationSelected failed", e);
      }
      renderViewports();
    };
    eventTarget.addEventListener(
      csToolsEnums.Events.ANNOTATION_SELECTION_CHANGE,
      handleSelectionChange
    );
    eventTarget.addEventListener(
      csToolsEnums.Events.ANNOTATION_COMPLETED,
      handleCompleted
    );
    return () => {
      eventTarget.removeEventListener(
        csToolsEnums.Events.ANNOTATION_SELECTION_CHANGE,
        handleSelectionChange
      );
      eventTarget.removeEventListener(
        csToolsEnums.Events.ANNOTATION_COMPLETED,
        handleCompleted
      );
    };
  }, []);

  return (
    <div
      className={`cursor-pointer ${!viewportLoadingComplete && "disable-toolbar"}`}
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
      }}
    >
      {/* The bottom bar stays the SAME whether or not MPR is active. MPR is
          entered/closed from the "More" menu (MPR item) — MedDream-style — so the
          toolbar doesn't swap layouts when MPR turns on. */}
      <>
          {/* Window Level */}
          <div style={{ ...toolBarFlex, height: "100%" }}>
            <Tool tool={WINDOW_LEVEL_TOOL} />
          </div>

          {/* Measure (dropdown) */}
          <div ref={measureRef} style={{ height: "100%", ...toolBarFlex }}>
            <div
              onClick={() => setShowMeasureSheet((prev) => !prev)}
              style={{ ...toolStyle }}
            >
              <div style={{ ...iconBoxStyle, gap: "4px" }}>
                <div className="highlight-tool-mobile">
                  <MeasureAngle
                    color={
                      isMeasureActive
                        ? theme.palette.activeTool
                        : theme.palette.inactiveTool
                    }
                  />
                </div>
                <DropDown
                  up
                  color={
                    isMeasureActive
                      ? theme.palette.activeTool
                      : theme.palette.inactiveTool
                  }
                />
              </div>
              <span style={mobileLabelStyle}>{t("viewer.tools.measure")}</span>
            </div>
            {showMeasureSheet && <MobileMeasureSheet anchorRef={measureRef} />}
          </div>

          {/* Rotate (dropdown: rotate/flip) */}
          <MobileRotateMenu />

          {/* Layout (dropdown) */}
          <MobileLayoutMenu />
      </>

      {showKeyImagesAndTextAnnotation && (
        <div
          className="cursor-pointer"
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <KeyImages />
        </div>
      )}

      {/* More stays to the RIGHT of Key Images (anchored popup above the button).
          Always shown — including during MPR — since MPR is managed from inside it. */}
      <MobileMoreMenu />
    </div>
  );
};

export default MobileToolbar;
